import { Checkbox, Group, Popover, Stack } from '@mantine/core';
import type {
	FeatureInformation,
	PrimitivePermissionType,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { IconCheck, IconMinus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { AccessSummaryButton } from './AccessSummaryButton';
import { MenuItem } from './MenuItem';
import { computeManageOptions } from './permissionUtils';
import type { IamRoleFormValues } from './types';

const FACE_VALUE_TO_ICON = {
	Manage: <IconCheck size={14} />,
	Custom: <IconMinus size={14} />,
	'No access': null,
	'View only': null,
};

export function PermissionSelect({
	form,
	feature,
	disabled = false,
	resourceFiltersIdx,
	isTeamFilter = false,
}: {
	form: IamRoleFormValues;
	feature: FeatureInformation;
	disabled?: boolean;
	resourceFiltersIdx?: number;
	isTeamFilter?: boolean;
}) {
	const optionTextColor = disabled ? 'text/primary/disabled' : undefined;

	const hasMinimumViewOnlyRequirement = [
		'Integrations',
		'Teams',
		'Users',
		'Resources',
		'Properties',
	].includes(feature.name);

	const onlyHasViewOnlyPermission = ['Preview'].includes(feature.name);

	const selectedPermissions =
		typeof resourceFiltersIdx === 'number'
			? new Set(
					form.values.resources_filter_configs[resourceFiltersIdx].permissions
				)
			: new Set(form.values.permissions);

	const availablePermissions = feature.permissions.filter((p) => {
		if (typeof resourceFiltersIdx === 'number') {
			return p.name !== 'Resources.Create';
		} else {
			return p.name !== 'Resources.CreateInTeams';
		}
	});

	const faceValue = useMemo(() => {
		const featurePermissions = availablePermissions.map((p) => p.name);

		const featureSelectedPermissions = featurePermissions.filter((p) =>
			selectedPermissions.has(p)
		);

		const hasNoPermissions = featureSelectedPermissions.length === 0;
		if (hasNoPermissions) {
			return 'No access';
		}

		const hasAllPermissions =
			featureSelectedPermissions.length === featurePermissions.length;
		if (hasAllPermissions && !onlyHasViewOnlyPermission) {
			return 'Manage';
		}

		// If only Read is selected, return View only
		const hasOnlyRead =
			featureSelectedPermissions.length === 1 &&
			featureSelectedPermissions.includes(
				(feature.name + '.Read') as PrimitivePermissionType
			);

		if (hasOnlyRead || onlyHasViewOnlyPermission) {
			return 'View only';
		}

		// Some permissions are selected but not all
		return 'Custom';
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		selectedPermissions,
		onlyHasViewOnlyPermission,
		feature.permissions,
		feature.name,
	]);

	const disableCreateInTeams =
		disabled ||
		(feature.name === 'Resources' &&
			typeof resourceFiltersIdx === 'number' &&
			!isTeamFilter);

	const manageOptions = useMemo(
		() => computeManageOptions(feature, resourceFiltersIdx),
		[feature, resourceFiltersIdx]
	);

	const setManage = () => {
		const currentPermissionsSet =
			typeof resourceFiltersIdx === 'number'
				? new Set(
						form.values.resources_filter_configs[resourceFiltersIdx].permissions
					)
				: new Set(form.values.permissions);

		availablePermissions.forEach((p) => {
			// Skip CreateInTeams if it's disabled
			if (p.name === `${feature.name}.CreateInTeams` && disableCreateInTeams) {
				return;
			}
			currentPermissionsSet.add(p.name);
		});

		if (typeof resourceFiltersIdx === 'number') {
			form.setFieldValue(
				`resources_filter_configs.${resourceFiltersIdx}.permissions`,
				Array.from(currentPermissionsSet)
			);
		} else {
			form.setFieldValue('permissions', Array.from(currentPermissionsSet));
		}
	};

	const setViewOnly = () => {
		const currentPermissionsSet =
			typeof resourceFiltersIdx === 'number'
				? new Set(
						form.values.resources_filter_configs[resourceFiltersIdx].permissions
					)
				: new Set(form.values.permissions);

		const readPermission = `${feature.name}.Read`;

		availablePermissions.forEach((p) => {
			currentPermissionsSet.delete(p.name);
		});

		currentPermissionsSet.add(readPermission);

		if (typeof resourceFiltersIdx === 'number') {
			form.setFieldValue(
				`resources_filter_configs.${resourceFiltersIdx}.permissions`,
				Array.from(currentPermissionsSet)
			);
		} else {
			form.setFieldValue('permissions', Array.from(currentPermissionsSet));
		}
	};

	const setNoAccess = () => {
		const currentPermissionsSet =
			typeof resourceFiltersIdx === 'number'
				? new Set(
						form.values.resources_filter_configs[resourceFiltersIdx].permissions
					)
				: new Set(form.values.permissions);

		availablePermissions.forEach((p) => {
			currentPermissionsSet.delete(p.name);
		});

		if (typeof resourceFiltersIdx === 'number') {
			form.setFieldValue(
				`resources_filter_configs.${resourceFiltersIdx}.permissions`,
				Array.from(currentPermissionsSet)
			);
		} else {
			form.setFieldValue('permissions', Array.from(currentPermissionsSet));
		}
	};

	const toggleManageOption = (option: string) => {
		const currentPermissionsSet =
			typeof resourceFiltersIdx === 'number'
				? new Set(
						form.values.resources_filter_configs[resourceFiltersIdx].permissions
					)
				: new Set(form.values.permissions);

		const permission = `${feature.name}.${option}`;
		const readPermission = `${feature.name}.Read`;

		// If CreateInTeams is being disabled, remove it from permissions
		if (option === 'CreateInTeams' && disableCreateInTeams) {
			currentPermissionsSet.delete(permission);
		} else if (currentPermissionsSet.has(permission)) {
			currentPermissionsSet.delete(permission);
		} else {
			currentPermissionsSet.add(permission);
			currentPermissionsSet.add(readPermission);
		}

		if (typeof resourceFiltersIdx === 'number') {
			form.setFieldValue(
				`resources_filter_configs.${resourceFiltersIdx}.permissions`,
				Array.from(currentPermissionsSet)
			);
		} else {
			form.setFieldValue('permissions', Array.from(currentPermissionsSet));
		}
	};

	return (
		<Popover
			position="bottom-end"
			withinPortal
			styles={(theme) => ({
				dropdown: {
					boxShadow: theme.shadows.md,
					borderRadius: theme.radius.md,
					backgroundColor: disabled
						? theme.other.getColor('surface/secondary/default')
						: undefined,
				},
			})}
		>
			<Popover.Target>
				<AccessSummaryButton
					disabled={disabled}
					faceValue={
						feature.name === 'Resources' &&
						form.values.resources_filter_configs.length > 0 &&
						typeof resourceFiltersIdx !== 'number'
							? 'Custom'
							: faceValue
					}
				/>
			</Popover.Target>
			<Popover.Dropdown p={0}>
				<Stack spacing={0} p="xs">
					{feature.name === 'Resources' &&
					typeof resourceFiltersIdx !== 'number' &&
					form.values.resources_filter_configs.length > 0 ? (
						<MenuItem
							onClick={() => toggleManageOption('Create')}
							disabled={disabled}
						>
							<Group spacing="xs" noWrap>
								<Checkbox
									size="sm"
									disabled={disabled}
									checked={selectedPermissions.has(`${feature.name}.Create`)}
								/>
								<Text size="sm" weight="semibold" color={optionTextColor}>
									Create
								</Text>
							</Group>
						</MenuItem>
					) : (
						<>
							{!onlyHasViewOnlyPermission && (
								<>
									<MenuItem
										onClick={setManage}
										disabled={disabled}
										rightSection={FACE_VALUE_TO_ICON[faceValue]}
									>
										<Text size="sm" weight="semibold" color={optionTextColor}>
											Manage
										</Text>
									</MenuItem>
									<Stack
										spacing={0}
										sx={{
											height: ['Manage', 'Custom'].includes(faceValue)
												? manageOptions.length * 36
												: 0,
											overflow: 'hidden',
											transition: 'height 100ms ease',
										}}
									>
										{manageOptions.map((option) => (
											<MenuItem
												key={option.reducedName}
												onClick={() => toggleManageOption(option.reducedName)}
												disabled={
													disabled ||
													(option.reducedName === 'CreateInTeams' &&
														disableCreateInTeams)
												}
												tooltip={
													option.reducedName === 'CreateInTeams' &&
													disableCreateInTeams
														? 'Create is disabled until a team filter is added'
														: undefined
												}
											>
												<Group spacing="xs" noWrap>
													<Checkbox
														size="sm"
														disabled={
															disabled ||
															(option.reducedName === 'CreateInTeams' &&
																disableCreateInTeams)
														}
														checked={
															selectedPermissions.has(
																`${feature.name}.${option.reducedName}`
															) &&
															!(
																option.reducedName === 'CreateInTeams' &&
																disableCreateInTeams
															)
														}
													/>
													<Text
														size="sm"
														weight="semibold"
														color={
															disabled ||
															(option.reducedName === 'CreateInTeams' &&
																disableCreateInTeams)
																? 'text/primary/disabled'
																: undefined
														}
													>
														{option.reducedName === 'CreateInTeams'
															? 'Create'
															: option.reducedName}
													</Text>
												</Group>
											</MenuItem>
										))}
									</Stack>
								</>
							)}
							<MenuItem
								onClick={setViewOnly}
								rightSection={
									faceValue === 'View only' && <IconCheck size={14} />
								}
								disabled={disabled}
							>
								<Text size="sm" weight="semibold" color={optionTextColor}>
									View only
								</Text>
							</MenuItem>
							{typeof resourceFiltersIdx !== 'number' &&
								!hasMinimumViewOnlyRequirement && (
									<MenuItem
										onClick={setNoAccess}
										rightSection={
											faceValue === 'No access' && <IconCheck size={14} />
										}
										disabled={disabled}
									>
										<Text size="sm" weight="semibold" color={optionTextColor}>
											No access
										</Text>
									</MenuItem>
								)}
						</>
					)}
				</Stack>
			</Popover.Dropdown>
		</Popover>
	);
}
